<script setup lang="ts">
import DatoSectionContainer from '~/components/dato/DatoSectionContainer.vue'
import VSelect from '~/components/ui/Inputs/VSelect.vue'

import { usePageChartsTags } from '~/composables/usePageCharts'

import { isNonNullAndDefined } from '~/utils/type-guards'

import type { ChartsHeaderFragment } from '~/graphql/generated'

type Props = ChartsHeaderFragment
const props = defineProps<Props>()

const localePath = useLocalePath()
const { selectedCountryName, sortedAvailableCountries } =
  await usePageChartsTags()

const svgOffsetStyle = computed(() => {
  if (
    !isNonNullAndDefined(props.svgOffset) ||
    !isNonNullAndDefined(props.svgOffsetUnit)
  )
    return {}
  return {
    transform: `translateX(${props.svgOffset}${props.svgOffsetUnit})`,
  }
})

function handleCountryChange(country: string) {
  if (country === 'all') return navigateTo(localePath('/charts/'))
  return navigateTo(localePath(`/charts/${country}/`))
}
</script>

<template>
  <div class="tw-bg-fill-alt">
    <div
      class="tw-relative tw-z-10 tw-min-h-[113px] tw-w-full md:tw-min-h-[285px] lg:tw-min-h-[340px]"
    >
      <div
        class="tw-relative tw-z-[-1] tw-h-[113px] tw-w-full tw-overflow-hidden tw-bg-orange-500 md:tw-absolute md:tw-h-[285px] lg:tw-h-[340px]"
      >
        <div
          class="tw-absolute tw-left-0 tw-top-0 tw-h-full tw-w-1/2 tw-bg-fill-alt md:tw-w-3/4 lg:tw-w-[80%]"
        />
        <div
          class="tw-absolute -tw-top-xl tw-left-1/2 tw-h-[150px] tw-w-[150px] tw-origin-center -tw-translate-x-1/2 tw-transform md:-tw-top-md md:tw-left-3/4 md:tw-h-[314px] md:tw-w-[314px] lg:-tw-top-[37px] lg:tw-left-[80%] lg:tw-h-[414px] lg:tw-w-[414px]"
        >
          <img
            v-if="isNonNullAndDefined(svg?.url)"
            :style="svgOffsetStyle"
            :src="svg?.url"
            alt=""
            role="img"
          />
        </div>
      </div>
      <DatoSectionContainer class="tw-z-50 md:tw-min-w-[360px]" has-no-padding>
        <div
          class="tw-relative tw-w-full tw-pb-2xl tw-pt-4xl sm:tw-pb-4xl lg:tw-pb-[80px] lg:tw-pt-[56px]"
        >
          <div
            class="tw-grid tw-w-full tw-gap-3xl md:tw-w-[360px] lg:tw-w-[469px] xl:tw-w-[587px]"
          >
            <div class="tw-text-white-warm">
              <h1 class="tw-text-h2 lg:tw-text-h1">{{ title }}</h1>
              <div class="tw-text-base">{{ subtitle }}</div>
            </div>
            <VSelect
              class="selectOverrides tw-text-white-warm"
              :label="$t('charts.header.countrySelectLabel')"
              :available="['all', ...sortedAvailableCountries]"
              :model-value="selectedCountryName"
              :transform-label-fn="
                (countryName) => $t(`charts.tags.country.${countryName}`)
              "
              @update:model-value="handleCountryChange"
            />
          </div>
        </div>
      </DatoSectionContainer>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.selectOverrides :deep(.selectInput) {
  @apply tw-bg-fill-alt focus-visible:tw-outline-focus-inverse;

  .text,
  [icon] {
    @apply tw-text-white-warm;
  }
}
</style>
